import { ConfirmDeleteDialog } from '@/components/features/shared/ConfirmDeleteDialog'
import { LoaderElement } from '@/components/features/shared/LoaderElement'
import { NavHeader } from '@/components/features/shared/NavHeader'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { AccountStatus } from '@/domain/accounts/account'
import {
  AccountReportView,
  PERIOD_LABELS,
  ReportPeriodType,
  resolvePeriodDates,
} from '@/domain/reports/report'
import { useAccounts, useMovementChart } from '@/hooks/api/useAccounts'
import { useDeleteReportView, useReportViews } from '@/hooks/api/useReports'
import {
  BarChart3,
  Calendar,
  Layers,
  Pencil,
  Plus,
  Trash2,
  TrendingUp,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { MinimalAccountSelector } from '@/components/features/reports/MinimalAccountSelector'
import { AccountSeries, MultiAccountChart } from '@/components/features/reports/MultiAccountChart'
import { ReportViewDialog } from '@/components/features/reports/ReportViewDialog'
import { PageContainer } from '@/components/features/shared/PageContainer'

const SingleAccountChartFetcher = ({
  accountId,
  label,
  startDate,
  endDate,
  onDataLoaded,
}: {
  accountId: string
  label: string
  startDate: Date
  endDate: Date
  onDataLoaded: (series: AccountSeries) => void
}) => {
  const { data } = useMovementChart({
    accountId,
    filters: {
      accountId,
      startDate,
      endDate,
    },
  })

  useEffect(() => {
    if (data) {
      onDataLoaded({ accountId, label, data })
    }
  }, [data, accountId, label, onDataLoaded])

  return null
}

export const MainAccountsReport = () => {
  const { data: accountsResponse, isLoading: isLoadingAccounts } = useAccounts({
    limit: 1000,
    isMain: true,
    isCollector: false,
    status: AccountStatus.ACTIVE,
  })

  const mainAccounts = accountsResponse?.data || []

  const { data: savedViews = [], isLoading: isLoadingViews } = useReportViews()
  const deleteMutation = useDeleteReportView()

  const [selectedViewId, setSelectedViewId] = useState<string | null>(null)
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([])
  const [activePeriod, setActivePeriod] = useState<ReportPeriodType>(
    ReportPeriodType.LAST_30_DAYS
  )

  const [dialogOpen, setDialogOpen] = useState(false)
  const [viewToEdit, setViewToEdit] = useState<AccountReportView | null>(null)
  const [deleteId, setDeleteId] = useState<string | null>(null)

  const [seriesMap, setSeriesMap] = useState<Record<string, AccountSeries>>({})

  const currentView = useMemo(() => {
    return savedViews.find((v) => v.id === selectedViewId) || null
  }, [savedViews, selectedViewId])

  const handleSelectView = (viewId: string) => {
    if (viewId === 'custom') {
      setSelectedViewId(null)
      return
    }
    const view = savedViews.find((v) => v.id === viewId)
    if (view) {
      setSelectedViewId(view.id)
      setSelectedAccountIds(view.accountIds || [])
      setActivePeriod(view.periodType || ReportPeriodType.LAST_30_DAYS)
      setSeriesMap({})
    }
  }

  const handleToggleAccount = (id: string) => {
    setSelectedAccountIds((prev) => {
      const next = prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
      return next
    })
  }

  const handleSelectAll = () => {
    setSelectedAccountIds(mainAccounts.map((a) => a.id))
  }

  const handleClearAll = () => {
    setSelectedAccountIds([])
    setSeriesMap({})
  }

  const handleOpenCreate = () => {
    setViewToEdit(null)
    setDialogOpen(true)
  }

  const handleOpenEdit = (view: AccountReportView) => {
    setViewToEdit(view)
    setDialogOpen(true)
  }

  const handleDeleteConfirm = () => {
    if (deleteId) {
      deleteMutation.mutate(deleteId, {
        onSuccess: () => {
          if (selectedViewId === deleteId) {
            setSelectedViewId(null)
          }
          setDeleteId(null)
        },
      })
    }
  }

  const { from: startDate, to: endDate } = useMemo(() => {
    return resolvePeriodDates(activePeriod)
  }, [activePeriod])

  const handleDataLoaded = (newSeries: AccountSeries) => {
    setSeriesMap((prev) => {
      if (prev[newSeries.accountId]?.data === newSeries.data) return prev
      return { ...prev, [newSeries.accountId]: newSeries }
    })
  }

  const activeSeries = useMemo(() => {
    return selectedAccountIds
      .map((id) => seriesMap[id])
      .filter((s): s is AccountSeries => !!s)
  }, [selectedAccountIds, seriesMap])

  if (isLoadingAccounts || isLoadingViews) {
    return <LoaderElement />
  }

  return (
    <PageContainer>
      <NavHeader hideBackButton>
        <div className="flex flex-col gap-1">
          <h1 className="text-3xl font-bold tracking-tight">Reportes de Cuentas Principales</h1>
          <p className="text-sm text-muted-foreground">
            Comparativa de evolución de saldos entre cuentas seleccionadas a través de vistas personalizadas.
          </p>
        </div>
      </NavHeader>

      {/* Hidden fetchers to retrieve data for each selected account */}
      {selectedAccountIds.map((accId) => {
        const account = mainAccounts.find((a) => a.id === accId)
        const label = account
          ? `${account.accountNumber} - ${account.accountAlias || 'Principal'}`
          : accId
        return (
          <SingleAccountChartFetcher
            key={`${accId}-${activePeriod}`}
            accountId={accId}
            label={label}
            startDate={startDate}
            endDate={endDate}
            onDataLoaded={handleDataLoaded}
          />
        )
      })}

      {/* Views toolbar */}
      <Card>
        <CardHeader className="pb-3 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1">
            <CardTitle className="text-lg flex items-center gap-2">
              <Layers className="h-5 w-5 text-primary" />
              Vistas Guardadas
            </CardTitle>
            <CardDescription>
              Seleccioná una vista preconfigurada o armá una comparación personalizada al instante.
            </CardDescription>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="w-[220px]">
              <Select
                value={selectedViewId || 'custom'}
                onValueChange={(val) => handleSelectView(val)}
              >
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue placeholder="Seleccionar vista..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">-- Vista Personalizada --</SelectItem>
                  {savedViews.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {currentView && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleOpenEdit(currentView)}
                  className="h-9 px-2.5"
                >
                  <Pencil className="h-3.5 w-3.5 mr-1" />
                  Editar
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setDeleteId(currentView.id)}
                  className="h-9 px-2 text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </>
            )}

            <Button
              size="sm"
              onClick={handleOpenCreate}
              className="h-9 bg-primary text-primary-foreground"
            >
              <Plus className="h-4 w-4 mr-1" />
              Guardar Vista
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-4 pt-0">
          {/* Period selector */}
          <div className="flex flex-wrap items-center justify-between border-t pt-3 gap-2">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-xs font-semibold text-muted-foreground uppercase">
                Período Relativo:
              </span>
              <div className="flex flex-wrap gap-1">
                {Object.values(ReportPeriodType).map((period) => {
                  const isActive = activePeriod === period
                  return (
                    <Button
                      key={period}
                      type="button"
                      variant={isActive ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        setActivePeriod(period)
                        setSeriesMap({})
                      }}
                      className="h-7 text-xs px-2.5"
                    >
                      {PERIOD_LABELS[period]}
                    </Button>
                  )
                })}
              </div>
            </div>

            {currentView?.description && (
              <p className="text-xs text-muted-foreground italic max-w-md truncate">
                {currentView.description}
              </p>
            )}
          </div>

          {/* Account Selector */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <BarChart3 className="h-3.5 w-3.5" />
                Cuentas a Comparar
              </span>
            </div>
            <MinimalAccountSelector
              accounts={mainAccounts}
              selectedIds={selectedAccountIds}
              onToggle={handleToggleAccount}
              onSelectAll={handleSelectAll}
              onClearAll={handleClearAll}
            />
          </div>
        </CardContent>
      </Card>

      {/* Chart Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              Comparativa de Saldos por Cuenta
            </CardTitle>
            <CardDescription>
              Evolución temporal del saldo de cada cuenta principal seleccionada ({PERIOD_LABELS[activePeriod].toLowerCase()})
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {selectedAccountIds.length} líneas en gráfico
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {selectedAccountIds.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground border border-dashed rounded-lg">
              <BarChart3 className="h-10 w-10 text-muted-foreground/40 mb-2" />
              <p className="text-sm font-medium">No hay cuentas seleccionadas</p>
              <p className="text-xs text-muted-foreground mt-1">
                Seleccioná una o más cuentas principales arriba para generar el gráfico comparativo.
              </p>
            </div>
          ) : (
            <MultiAccountChart series={activeSeries} />
          )}
        </CardContent>
      </Card>

      {/* Dialog for Create/Edit View */}
      <ReportViewDialog
        isOpen={dialogOpen}
        onClose={() => {
          setDialogOpen(false)
          setViewToEdit(null)
        }}
        viewToEdit={viewToEdit}
        mainAccounts={mainAccounts}
        selectedAccountIds={selectedAccountIds}
        onSaved={(savedView) => {
          setSelectedViewId(savedView.id)
          setSelectedAccountIds(savedView.accountIds || [])
          setActivePeriod(savedView.periodType || ReportPeriodType.LAST_30_DAYS)
          setSeriesMap({})
        }}
      />

      {/* Confirm Delete Dialog */}
      <ConfirmDeleteDialog
        open={!!deleteId}
        onOpenChange={(open) => !open && setDeleteId(null)}
        onConfirm={handleDeleteConfirm}
        title="Eliminar Vista de Reporte"
        message="¿Estás seguro de que querés eliminar esta vista? Esta acción no se puede deshacer."
      />
    </PageContainer>
  )
}
